import React, { useState } from "react";

import { type Floor, type Planning } from "@eisox/api/core-api";
import { ActionButton, Heading, Loader, Modal } from "@eisox/design-system";
import { RoomIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useQuery } from "@tanstack/react-query";

import { roomApi } from "~/api";
import { organizePlanningsByWeekDays } from "~/features/planning";
import { m } from "~/paraglide/messages";
import type { EnhancedRoom } from "~/types/room.type";

import { Week } from "../../planning";
import { ModalRoomsSelector } from "./modal-rooms-selector";
import { Room } from "./room";
import { RoomPlanPicker } from "./room-plan-picker";

const NAME = "PlanningSelectorDialog";

interface PlanningSelectorDialogProps extends Modal.RootProps {
  floors: Floor[];
  rooms: EnhancedRoom[];
  onSelectPlannings?: (plannings: Planning[]) => void;
}

const PlanningSelectorDialog: React.FC<PlanningSelectorDialogProps> = ({
  floors,
  rooms,
  onSelectPlannings,
  open: openProp,
  onOpenChange,
  defaultOpen = false,
  children,
  ...props
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen,
  });
  const [selectableRooms, setSelectableRooms] = useState<EnhancedRoom[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string | undefined>(undefined);

  const { data, isLoading } = useQuery({
    ...roomApi.queries.plannings(selectedRoomId ?? ""),
    enabled: !!selectedRoomId,
  });

  const handleOpenChange = (open: boolean) => {
    setSelectedRoomId(undefined);
    setOpen(open);
  };

  const handleClickOnRoom = (id: string) => setSelectedRoomId(prev => (prev === id ? undefined : id));
  const handleSelectPlanning = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!data) return;
    onSelectPlannings?.(plannings);
    handleOpenChange(false);
  };

  const plannings = data ? data.filter(p => p.userDefined) : [];
  const { reorderedDays, timeslotsByDay } = organizePlanningsByWeekDays(plannings);

  return (
    <Modal.Root {...props} open={open} onOpenChange={handleOpenChange}>
      <Modal.Trigger asChild>
        <span className="font-medium cursor-pointer text-base text-primary-blue">{children}</span>
      </Modal.Trigger>
      <Modal.Content className="flex h-[700px] w-[1200px] flex-col">
        <Modal.Header title={m.planning_selector()} subtitle={m.select_room_to_import_planning()} icon={<RoomIcon />}>
          <Modal.Close asChild>
            <ActionButton variant="cancel">{m.cancel()}</ActionButton>
          </Modal.Close>
          <ActionButton disabled={!selectedRoomId} onClick={handleSelectPlanning}>
            {m.select()}
          </ActionButton>
        </Modal.Header>
        <div className="min-h-0 flex flex-1 gap-[40px]">
          <div className="min-h-0 flex max-w-[400px] min-w-[200px] flex-1 flex-col gap-[10px]">
            <Heading size={4} weight={"medium"}>
              {m.rooms()}
            </Heading>
            <ModalRoomsSelector floors={floors} rooms={rooms} onFilteredRoomsChange={setSelectableRooms} />
            <div className="min-h-0 flex flex-col gap-[5px] overflow-y-auto">
              {selectableRooms.map(room => (
                <Room
                  key={room.id}
                  name={room.name}
                  planName={room.floor.name}
                  selected={selectedRoomId === room.id}
                  onClick={() => handleClickOnRoom(room.id)}
                />
              ))}
            </div>
          </div>
          <div className="flex flex-1 flex-col justify-center gap-[10px]">
            {selectedRoomId ? (
              isLoading ? (
                <Loader className="self-center" />
              ) : (
                <Week.Root>
                  {reorderedDays.map(day => (
                    <Week.Day key={day} day={day as 0 | 1 | 2 | 3 | 4 | 5 | 6}>
                      {(timeslotsByDay[day] ?? []).map((timeslot, index) => (
                        <Week.Timeslot
                          key={index}
                          start={timeslot.hourStart}
                          end={timeslot.hourEnd}
                          type={timeslot.type}
                        />
                      ))}
                    </Week.Day>
                  ))}
                </Week.Root>
              )
            ) : (
              <RoomPlanPicker
                floors={floors}
                rooms={rooms}
                value={selectedRoomId ? [selectedRoomId] : []}
                onValueChange={value => {
                  if (value.length > 0) {
                    setSelectedRoomId(value[0]);
                  } else {
                    setSelectedRoomId(undefined);
                  }
                }}
              />
            )}
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};

PlanningSelectorDialog.displayName = NAME;

export { PlanningSelectorDialog };
export type { PlanningSelectorDialogProps };
